import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Logo } from "@/components/landing/Logo";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";
import { Toaster, toast } from "sonner";

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const AdminPage = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);
  const [loginHistory, setLoginHistory] = useState({ items: [], unique_ips: 0 });

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwdForm.next.length < 8) {
      toast.error("Minimo 8 caratteri.");
      return;
    }
    if (pwdForm.next !== pwdForm.confirm) {
      toast.error("Le password non coincidono.");
      return;
    }
    setSavingPwd(true);
    try {
      await api.post("/me/change-password", {
        current_password: pwdForm.current,
        new_password: pwdForm.next,
      });
      setPwdForm({ current: "", next: "", confirm: "" });
      setPwdOpen(false);
      toast.success("Password admin aggiornata.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel cambio password.");
    } finally {
      setSavingPwd(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) return nav("/login");
    if (!user.is_admin) return nav("/area");
    (async () => {
      try {
        const [l, s, h] = await Promise.all([
          api.get("/admin/waitlist"),
          api.get("/admin/stats"),
          api.get("/admin/login-history?limit=20"),
        ]);
        setList(l.data);
        setStats(s.data);
        setLoginHistory(h.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading, nav]);

  if (authLoading || loading) {
    return (
      <div className="section-dark min-h-screen flex items-center justify-center text-white/50 font-mono text-sm">
        Caricamento…
      </div>
    );
  }

  // Filter rows
  const q = filter.trim().toLowerCase();
  const rows = q
    ? list.filter((r) =>
        [r.nome, r.cognome, r.email, r.referral_code, r.referred_by]
          .join(" ")
          .toLowerCase()
          .includes(q)
      )
    : list;

  // Lookup map: code → full user (for referrer name resolution)
  const byCode = {};
  for (const r of list) byCode[r.referral_code] = r;
  const nameOf = (code) => {
    if (!code) return "—";
    if (code === "SAM") return "SAM · Fondatori";
    const u = byCode[code];
    if (!u) return code;
    return `${u.nome || ""} ${u.cognome || ""}`.trim() || code;
  };

  // Build children map for tree view
  const byParent = {};
  for (const r of list) {
    const p = r.referred_by || "ROOT";
    (byParent[p] ||= []).push(r);
  }
  const renderNode = (code, depth = 0) => {
    const kids = byParent[code] || [];
    return kids.map((k) => (
      <div
        key={k.id}
        className="border-l border-white/10 pl-4 ml-2"
        style={{ marginLeft: depth === 0 ? 0 : 10 }}
      >
        <div className="py-1.5 flex items-center gap-3">
          <span className="font-mono text-[11px] tracking-[0.14em] text-[color:var(--lif-accent)]">
            {k.referral_code}
          </span>
          <span className="font-body text-sm text-white">
            {k.nome} {k.cognome || ""}
          </span>
          <span className="text-white/40 text-xs">{k.email}</span>
        </div>
        {byParent[k.referral_code] && (
          <div className="ml-2">{renderNode(k.referral_code, depth + 1)}</div>
        )}
      </div>
    ));
  };

  return (
    <div className="section-dark min-h-screen">
      <Toaster theme="dark" position="bottom-right" />
      {/* Nav */}
      <nav className="px-6 lg:px-10 py-5 border-b border-white/10 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo size={22} />
          <span className="font-mono font-semibold text-[11px] tracking-[0.4em] text-white">
            L·I·F
          </span>
          <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--lif-accent)] ml-3">
            / Admin
          </span>
        </Link>
        <div className="flex items-center gap-5 font-mono text-[11px] tracking-[0.2em] uppercase text-white/60">
          <span className="hidden sm:inline">{user?.email}</span>
          <button
            onClick={() => setPwdOpen(true)}
            data-testid="admin-change-pwd-btn"
            className="hover:text-[color:var(--lif-accent)] transition-colors"
          >
            Password
          </button>
          <button
            onClick={() => {
              logout();
              nav("/");
            }}
            className="hover:text-[color:var(--lif-accent)] transition-colors"
          >
            Esci
          </button>
        </div>
      </nav>

      {/* Change-password modal */}
      {pwdOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-6"
          onClick={() => !savingPwd && setPwdOpen(false)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={changePassword}
            data-testid="admin-pwd-modal"
            className="w-full max-w-md bg-[#0a0a0d] border border-white/15 rounded-lg p-6 space-y-4"
          >
            <div className="flex items-center justify-between">
              <p className="eyebrow">Cambia password admin</p>
              <button
                type="button"
                onClick={() => setPwdOpen(false)}
                className="font-mono text-xs text-white/40 hover:text-white"
              >
                ✕
              </button>
            </div>
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/50 mb-2 block">
                Password attuale
              </span>
              <input
                type="password"
                required
                value={pwdForm.current}
                onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })}
                data-testid="admin-pwd-current"
                autoComplete="current-password"
                className="w-full bg-white/[0.03] border border-white/15 rounded-md px-4 py-3 text-white outline-none focus:border-[color:var(--lif-accent)]/60 transition-colors font-body"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/50 mb-2 block">
                Nuova password (min 8)
              </span>
              <input
                type="password"
                required
                minLength={8}
                value={pwdForm.next}
                onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })}
                data-testid="admin-pwd-new"
                autoComplete="new-password"
                className="w-full bg-white/[0.03] border border-white/15 rounded-md px-4 py-3 text-white outline-none focus:border-[color:var(--lif-accent)]/60 transition-colors font-body"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/50 mb-2 block">
                Conferma
              </span>
              <input
                type="password"
                required
                minLength={8}
                value={pwdForm.confirm}
                onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                data-testid="admin-pwd-confirm"
                autoComplete="new-password"
                className="w-full bg-white/[0.03] border border-white/15 rounded-md px-4 py-3 text-white outline-none focus:border-[color:var(--lif-accent)]/60 transition-colors font-body"
              />
            </label>
            <button
              type="submit"
              disabled={savingPwd}
              data-testid="admin-pwd-save"
              className="lif-btn w-full py-3"
            >
              {savingPwd ? "Salvataggio..." : "Aggiorna password"}
            </button>
          </form>
        </div>
      )}

      <div className="px-6 lg:px-10 py-10 max-w-[1400px] mx-auto">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Iscritti totali", value: stats?.total_signups ?? 0 },
            { label: "Con password", value: stats?.with_password ?? 0 },
            { label: "Acquisti corso", value: stats?.course_purchases ?? 0 },
            {
              label: "Target waitlist",
              value: `${stats?.total_signups ?? 0} / ${stats?.target ?? 100}`,
            },
          ].map((s) => (
            <div
              key={s.label}
              className="border border-white/10 rounded-lg p-5 bg-white/[0.02]"
            >
              <p className="eyebrow">{s.label}</p>
              <p className="mt-2 font-display font-bold text-white text-3xl">
                {s.value}
              </p>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2 font-mono text-[11px] tracking-[0.22em] uppercase text-white/60">
            <span>Avanzamento verso 100</span>
            <span>
              {Math.min(100, Math.round(((stats?.total_signups ?? 0) / (stats?.target ?? 100)) * 100))}%
            </span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-[color:var(--lif-accent)] transition-all"
              style={{
                width: `${Math.min(
                  100,
                  Math.round(((stats?.total_signups ?? 0) / (stats?.target ?? 100)) * 100)
                )}%`,
              }}
            />
          </div>
        </div>

        {/* Two columns: table + tree */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Table */}
          <div className="lg:col-span-3">
            <div className="flex items-end justify-between mb-4">
              <h2 className="font-display font-bold text-white text-2xl">
                Tutti gli iscritti
              </h2>
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Cerca nome, email, codice..."
                className="bg-white/[0.03] border border-white/15 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-[color:var(--lif-accent)]/60 w-72 font-body"
              />
            </div>
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white/[0.03] text-left font-mono text-[10px] tracking-[0.18em] uppercase text-white/50">
                    <th className="px-3 py-3">Nome</th>
                    <th className="px-3 py-3">Email</th>
                    <th className="px-3 py-3">Tel</th>
                    <th className="px-3 py-3">Codice</th>
                    <th className="px-3 py-3">Referente</th>
                    <th className="px-3 py-3">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className="border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-3 py-2.5 text-white">
                        {r.nome || "—"} {r.cognome || ""}
                        {r.is_admin && (
                          <span className="ml-2 font-mono text-[9px] tracking-[0.18em] uppercase text-[color:var(--lif-accent)]">
                            admin
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-white/70">{r.email}</td>
                      <td className="px-3 py-2.5 text-white/70">
                        {r.telefono || "—"}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-[11px] tracking-[0.14em] text-[color:var(--lif-accent)]">
                        {r.referral_code}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-white text-sm">
                          {nameOf(r.referred_by)}
                        </div>
                        {r.referred_by && (
                          <div className="font-mono text-[10px] tracking-[0.14em] text-white/40">
                            {r.referred_by}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-white/50 font-mono text-[11px]">
                        {fmtDate(r.created_at)}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-3 py-10 text-center text-white/40 font-body"
                      >
                        Nessun risultato
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tree */}
          <div className="lg:col-span-2">
            <h2 className="font-display font-bold text-white text-2xl mb-4">
              Albero referral
            </h2>
            <div className="border border-white/10 rounded-lg p-5 bg-white/[0.02] max-h-[600px] overflow-auto">
              <div className="font-mono text-[11px] tracking-[0.14em] text-[color:var(--lif-accent)] mb-2">
                SAM
                <span className="ml-2 text-white/50">· Codice fondatori</span>
              </div>
              {renderNode("SAM")}
              {Object.keys(byParent).length <= 1 && (
                <p className="text-white/40 text-sm mt-4">
                  Nessun invito ancora.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Admin login history */}
        <div className="mt-12" data-testid="admin-login-history">
          <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
            <h2 className="font-display font-bold text-white text-2xl">
              Accessi admin recenti
            </h2>
            <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/50">
              {loginHistory.unique_ips} IP distinti · ultimi {loginHistory.items.length}
            </p>
          </div>
          <div className="border border-white/10 rounded-lg overflow-hidden bg-white/[0.02]">
            {loginHistory.items.length === 0 ? (
              <p className="p-6 text-white/40 text-sm font-body">
                Nessun accesso registrato (iniziano da ora).
              </p>
            ) : (
              <table className="w-full text-sm font-body">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.03] font-mono text-[10px] tracking-[0.2em] uppercase text-white/50">
                    <th className="text-left px-4 py-3">Quando</th>
                    <th className="text-left px-4 py-3">IP</th>
                    <th className="text-left px-4 py-3 hidden sm:table-cell">Dispositivo</th>
                    <th className="text-left px-4 py-3">Metodo</th>
                    <th className="text-left px-4 py-3">Nuovo IP</th>
                  </tr>
                </thead>
                <tbody>
                  {loginHistory.items.map((r, i) => (
                    <tr
                      key={i}
                      className="border-b border-white/5 hover:bg-white/[0.02]"
                    >
                      <td className="px-4 py-3 text-white/80">
                        {fmtDate(r.created_at)}
                      </td>
                      <td className="px-4 py-3 font-mono text-[color:var(--lif-accent)] text-xs">
                        {r.ip}
                      </td>
                      <td className="px-4 py-3 text-white/60 hidden sm:table-cell">
                        {r.device || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/50">
                          {r.method === "trusted-device" ? "Dispositivo fidato" : "OTP"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {r.is_new_ip ? (
                          <span className="inline-block px-2 py-0.5 rounded border border-[color:var(--lif-accent)]/40 bg-[color:var(--lif-accent)]/10 font-mono text-[10px] tracking-[0.18em] uppercase text-[color:var(--lif-accent)]">
                            ⚠ Nuovo
                          </span>
                        ) : (
                          <span className="font-mono text-[10px] text-white/40">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <p className="mt-3 text-white/40 text-xs font-body">
            Ricevi una mail di avviso ogni volta che accedi da un IP mai usato prima.
            Nessun blocco automatico: puoi loggarti da qualsiasi cellulare o wifi.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
